import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, queryString } from '../api';
import { EmptyState, ErrorNotice, Spinner, formatDate } from '../components/ui';
import type { EmailDelivery } from '../types';

/**
 * Every send, with the address it went to and the message as it was actually
 * worded at the time. This is the screen that answers "I never got the
 * renewal reminder" in ten seconds.
 */
export function EmailLogPage() {
  const [params, setParams] = useSearchParams();
  const query = params.get('q') ?? '';
  const [draft, setDraft] = useState(query);
  const [rows, setRows] = useState<EmailDelivery[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<unknown>(null);
  const [open, setOpen] = useState<number | null>(null);
  const firstRender = useRef(true);

  useEffect(() => setDraft(query), [query]);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    if (draft === query) return;
    const timer = window.setTimeout(() => {
      setParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          if (draft === '') next.delete('q');
          else next.set('q', draft);
          return next;
        },
        { replace: true },
      );
    }, 250);
    return () => window.clearTimeout(timer);
  }, [draft, query, setParams]);

  useEffect(() => {
    setError(null);
    api
      .get<{ rows: EmailDelivery[]; total: number }>(`/email/deliveries${queryString({ q: query })}`)
      .then((result) => {
        setRows(result.rows);
        setTotal(result.total);
      })
      .catch(setError);
  }, [query]);

  return (
    <>
      <p className="muted small">
        <Link to="/email">← Back to email</Link>
      </p>

      <div className="page-head">
        <h1>Delivery log</h1>
        <p className="muted">{rows === null ? 'Loading…' : `${total} shown`}</p>
      </div>

      <div className="filters">
        <div className="field search">
          <label htmlFor="log-search">Search</label>
          <input
            id="log-search"
            type="search"
            placeholder="Member name, address or subject"
            value={draft}
            autoFocus
            onChange={(e) => setDraft(e.target.value)}
          />
        </div>
      </div>

      <ErrorNotice error={error} />

      {rows === null && error === null ? <Spinner label="Loading the log" /> : null}
      {rows !== null && rows.length === 0 ? (
        <EmptyState title="Nothing matches">Nothing has been emailed to anyone matching that.</EmptyState>
      ) : null}

      {rows !== null && rows.length > 0 ? (
        <table className="members">
          <thead>
            <tr>
              <th scope="col">Member</th>
              <th scope="col">Sent to</th>
              <th scope="col">Subject</th>
              <th scope="col">When</th>
              <th scope="col">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <DeliveryRow
                key={row.id}
                row={row}
                open={open === row.id}
                onToggle={() => setOpen(open === row.id ? null : row.id)}
              />
            ))}
          </tbody>
        </table>
      ) : null}
    </>
  );
}

function DeliveryRow({
  row,
  open,
  onToggle,
}: {
  row: EmailDelivery;
  open: boolean;
  onToggle: () => void;
}) {
  const [detail, setDetail] = useState<EmailDelivery | null>(null);

  useEffect(() => {
    if (!open || detail !== null) return;
    api
      .get<{ delivery: EmailDelivery }>(`/email/deliveries/${row.id}`)
      .then((result) => setDetail(result.delivery))
      .catch(() => undefined);
  }, [open, detail, row.id]);

  return (
    <>
      <tr className={open ? 'row-open' : ''}>
        <td data-label="Member">
          <Link to={`/members/${row.member_id}`}>{row.member_name}</Link>
        </td>
        <td data-label="Sent to">{row.email}</td>
        <td data-label="Subject">
          <button type="button" className="link" onClick={onToggle}>
            {row.subject}
          </button>
        </td>
        <td data-label="When">{formatDate(row.sent_at ?? row.queued_at)}</td>
        <td data-label="Status">
          <span className={`pill pill-delivery-${row.status}`}>{row.status}</span>
          {row.error ? <span className="muted block small">{row.error}</span> : null}
        </td>
      </tr>
      {open ? (
        <tr className="row-detail">
          <td colSpan={5}>
            {detail === null ? (
              <Spinner label="Loading the message" />
            ) : (
              <div className="email-preview">
                <p className="muted small">
                  Exactly as sent on {formatDate(detail.sent_at ?? detail.queued_at)} — not as the
                  template reads today.
                </p>
                <div dangerouslySetInnerHTML={{ __html: detail.body_html ?? '' }} />
              </div>
            )}
          </td>
        </tr>
      ) : null}
    </>
  );
}
